"use client";

import { getToolName, type ToolUIPart } from "ai";
import { type ReactElement, useEffect, useState } from "react";
import { Tool, ToolContent, ToolHeader } from "@/components/ai-elements/tool";
import type {
  ChatAddToolApprovalResponse,
  ChatAddToolResult,
  ChatRetryApprovalContinuation,
  ChatSendMessage,
} from "@/lib/ai/chat-message-renderer-contract";
import { getToolConfirmRegistry } from "@/lib/ai/tools/ui";
import { NativeToolApprovalCard } from "@/lib/ai/tools/ui/native-tool-approval-card";
import type { ToolRenderPlan } from "./render-model";

function hasApproval(part: ToolUIPart): part is ToolUIPart & {
  approval: { id: string; approved?: boolean; reason?: string };
} {
  return "approval" in part && Boolean(part.approval);
}

/** plugin / confirmation / custom output の standalone 描画を行う。 */
export function StandaloneToolPart({
  plan,
  isStreaming,
  addToolApprovalResponse,
  addToolResult,
  requestApprovalContinuation,
  canRetryApprovalContinuation = false,
  isRetryingApprovalContinuation = false,
  retryApprovalContinuation,
  retryableApprovalToolCallId,
}: {
  plan: ToolRenderPlan;
  isStreaming: boolean;
  addToolApprovalResponse: ChatAddToolApprovalResponse;
  addToolResult: ChatAddToolResult;
  /** confirmation 専用。複数承認の完了条件と continuation の集約を適用する。 */
  requestApprovalContinuation: ChatSendMessage;
  /** 通常メッセージ送信との互換用。confirmation continuation には使用しない。 */
  sendMessage: ChatSendMessage;
  canRetryApprovalContinuation?: boolean;
  isRetryingApprovalContinuation?: boolean;
  retryApprovalContinuation?: ChatRetryApprovalContinuation;
  retryableApprovalToolCallId?: string;
}): ReactElement | null {
  const needsConfirmation = plan.renderer === "confirmation";
  const [open, setOpen] = useState(needsConfirmation);
  useEffect(() => {
    if (
      needsConfirmation &&
      (plan.part.state === "input-available" ||
        plan.part.state === "approval-requested")
    ) {
      setOpen(true);
    }
  }, [needsConfirmation, plan.part.state]);

  if (plan.renderer === "plugin" || plan.renderer === "custom-output") {
    return <div className="min-w-0 w-full">{plan.node}</div>;
  }
  if (!needsConfirmation || plan.part.type === "dynamic-tool") return null;

  const toolName = getToolName(plan.part);
  const approval = hasApproval(plan.part) ? plan.part.approval : null;
  const isNativeApproval = approval !== null;
  const displayState =
    plan.part.state === "approval-responded" && approval?.approved === false
      ? "output-denied"
      : plan.part.state;
  const Confirm = !isNativeApproval ? getToolConfirmRegistry()[toolName] : null;
  if (!isNativeApproval && !Confirm) return null;

  return (
    <Tool onOpenChange={setOpen} open={open}>
      <ToolHeader state={displayState} type={plan.part.type} />
      <ToolContent defer={isStreaming}>
        {isNativeApproval ? (
          <NativeToolApprovalCard
            part={plan.part}
            toolName={toolName}
            addToolApprovalResponse={addToolApprovalResponse}
            addToolResult={addToolResult}
            canRetryApprovalContinuation={canRetryApprovalContinuation}
            approveLabel={plan.approvalReview?.approveLabel}
            approveVariant={plan.approvalReview?.approveVariant}
            denyLabel={plan.approvalReview?.denyLabel}
            resultContent={plan.node}
            isStreaming={isStreaming}
            isRetryingApprovalContinuation={isRetryingApprovalContinuation}
            reviewContent={plan.approvalReview?.node}
            sendMessage={requestApprovalContinuation}
            retryApprovalContinuation={retryApprovalContinuation}
            retryableApprovalToolCallId={retryableApprovalToolCallId}
          />
        ) : (
          Confirm && (
            <Confirm
              part={plan.part}
              addToolResult={addToolResult}
              sendMessage={requestApprovalContinuation}
            />
          )
        )}
      </ToolContent>
    </Tool>
  );
}
